import i18next from "src/i18n/config";

import TimeService from "@services/timeService/timeService";

import { Device, DeviceState } from "../types";

export type DeviceConnectionType = "ethernet" | "wifi" | "cellular";

export const getActiveConnectionType = (networks: DeviceState["networks"], isOnline: boolean): DeviceConnectionType | null => {
    if (!networks || !isOnline) {
        return null;
    }

    const hasEthernet = networks.some((net) => net.interface.startsWith("eth") && net.ips.length > 0);
    if (hasEthernet) {
        return "ethernet";
    }

    const hasWiFi = networks.some((net) => net.interface.startsWith("wlan") && net.ips.length > 0);
    if (hasWiFi) {
        return "wifi";
    }

    const hasCellular = networks.some((net) => net.interface.startsWith("wwan") && net.ips.length > 0);
    if (hasCellular) {
        return "cellular";
    }

    return null;
};

// Keys are spelled out instead of interpolated so that i18next-cli can still extract them.
export const getDeviceConnectionLabel = (type: DeviceConnectionType | null): string | null => {
    switch (type) {
        case "ethernet":
            return i18next.t("pages:devicePage.connection.ethernet");
        case "wifi":
            return i18next.t("pages:devicePage.connection.wifi");
        case "cellular":
            return i18next.t("pages:devicePage.connection.cellular");
        default:
            return null;
    }
};

export const getDeviceConnectionIcon = (type: DeviceConnectionType | null, isOnline: boolean): string => {
    if (!isOnline) {
        return "fa-plug-circle-xmark";
    }

    switch (type) {
        case "ethernet":
            return "fa-network-wired";
        case "wifi":
            return "fa-wifi";
        case "cellular":
            return "fa-signal";
        default:
            return "fa-plug-circle-check";
    }
};

// Android reports these instead of the real address when the caller lacks the privileged
// permission, so treat them as "unknown" rather than showing a MAC that identifies nothing.
const placeholderMacs = new Set(["00:00:00:00:00:00", "02:00:00:00:00:00"]);

const isUsableMac = (mac: string | null | undefined): mac is string => !!mac && !placeholderMacs.has(mac.toLowerCase());

const getNetworkMac = (networks: DeviceState["networks"], prefix: string): string | null => {
    const network = networks?.find((net) => net.interface.startsWith(prefix));
    return network?.hw || null;
};

export const getUsableNetworkMac = (networks: DeviceState["networks"], prefix: string): string | null => {
    const mac = getNetworkMac(networks, prefix);
    return isUsableMac(mac) ? mac : null;
};

// The WiFi MAC has two independent sources: the WiFi status block and the hardware address of the
// wlan interface. A device may report only one of them, so fall back to the interface list when
// the WiFi status has nothing usable.
export const getWifiMac = (state: DeviceState | null | undefined): string | null => {
    const wifiStatusMac = state?.wifi?.mac;
    if (isUsableMac(wifiStatusMac)) {
        return wifiStatusMac;
    }

    return getUsableNetworkMac(state?.networks, "wlan");
};

export const getDeviceUptime = (device: Device | null) => {
    if (!device?.isOnline) {
        return null;
    }

    const uptimeMs = device.lastState?.datetime?.elapsedMs;

    if (!uptimeMs) {
        return null;
    }

    const deviceCurrentMs = device.lastState?.datetime?.currentMs;
    const zone = device.lastState?.properties?.["persist.sys.timezone"] || "UTC";

    if (!deviceCurrentMs) {
        return null;
    }

    const bootTime = TimeService.getFromMillis(deviceCurrentMs - uptimeMs);
    return TimeService.getHumanReadableTimeDifference(bootTime, { zone });
};

export const getDeviceOnlineSince = (device: Device | null) => {
    if (!device?.isOnline || !device.lastOnlineDate) {
        return null;
    }
    return TimeService.getHumanReadableTimeDifference(device.lastOnlineDate, { zone: "UTC" });
};
